'use client';

import { Badge } from '@ahafood/ui/components/badge';
import { Button } from '@ahafood/ui/components/button';
import { Card } from '@ahafood/ui/components/card';
import { Image } from '@ahafood/ui/components/image';
import { assetPrefix } from '@ahm/api-wrappers-core';
import { formatCurrency } from '@ahm/common-helpers';
import { Star } from 'lucide-react';
import { useCallback, useMemo } from 'react';
import { VideoPreviewCard, type VideoPreviewMode } from './video-preview-card';
import TrendingIcon from '@/../public/static/icons/trending-icon.svg';
import type { SoloMealItem } from '@/api/use-get-solomeal-item';
import { ItemFrame } from '@/components/item-frame';
import { useAddToCartSheet } from '@/hooks/use-add-to-cart-sheet';
import { useCityRoutes } from '@/hooks/use-city-navigation';
import { useItemImpression } from '@/hooks/use-item-impression';
import { usePathname, useRouter } from '@/i18n/routing';
import {
  calculateETA,
  formatDistanceMeterKilometer,
} from '@/lib/eta-calculator';
import {
  extractSoloMealMetadata,
  soloMealItemToMenuItem,
} from '@/lib/item-converters';
import { useAiFoodOrderStore } from '@/store';

const InactiveBadge = () => (
  <div className="absolute inset-0 bg-neutral-50/50 z-10 rounded-xl">
    <Badge className="absolute bg-neutral-90 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 px-3 py-1.5 shadow-md md:px-4 md:py-2 md:text-sm font-bold min-w-max">
      Tạm ngưng
    </Badge>
  </div>
);

export default function SoloMealItemCard({
  item,
  index,
  isAuthenticated,
  setShowLoginDialog,
  onClick,
  onAddToCartClick,
}: {
  item: SoloMealItem;
  index: number;
  isAuthenticated: boolean;
  setShowLoginDialog: (show: boolean) => void;
  onClick?: () => void;
  onAddToCartClick?: () => void;
}) {
  // Logic xen kẽ video auto trái/phải trên mobile (2 cột):
  // - Nhóm chẵn (0, 2, 4...): video ở cuối nhóm (index 3, 11, 19...) - bên phải
  // - Nhóm lẻ (1, 3, 5...): video ở đầu nhóm (index 4, 12, 20...) - bên trái
  const groupIndex = Math.floor(index / 4);
  const positionInGroup = index % 4;
  const isAutoVideo =
    groupIndex % 2 === 0 ? positionInGroup === 3 : positionInGroup === 2;
  const videoMode: VideoPreviewMode = isAutoVideo ? 'auto' : 'hover';
  const pathName = usePathname();
  const isSoloMeal = pathName.includes('solomeal');

  const { openCartSheet } = useAddToCartSheet();
  const routes = useCityRoutes();
  const router = useRouter();
  const cityId = useAiFoodOrderStore.use.city_id();
  const isClosed = !item.is_available;

  // Track impression when item visible in viewport (80%)
  const impressionRef = useItemImpression({
    itemId: item._id,
    buttonName: 'solomeal_card_impression',
    buttonLocation: 'solomeal_list',
    eventParams: {
      store_id: item.store_id.toString(),
      item_id: item._id,
      item_name: item.name,
      item_fee: item.discounted_price,
      item_discount: item.price - item.discounted_price,
      distance: item.distance,
      rating: item.rating?.average ?? item.rating?.google_average ?? 0,
      store_name: item.store_name,
      store_total_sold: item.store_total_sold,
      item_total_sold: item.item_total_sold,
      trend_item: item.trend_item ?? false,
      is_available: item.is_available,
      item_index: index, // Vị trí của item trong list
    },
  });

  const { showRating, rawRating, showSold, soldDisplay, imageSrc } =
    useMemo(() => {
      const sold = item.store_total_sold;
      const showSold = sold > 10;
      const soldDisplay =
        sold > 999 ? `${(sold / 1000).toFixed(1)}K` : sold.toString();
      const rating =
        item.rating?.average ||
        (item.rating?.google_average >= 4 ? item.rating.google_average : null);

      // Optimize image source selection
      const imageSrc =
        item.image_preview_url && item.image_preview_url !== ''
          ? item.image_preview_url
          : item.image_thumb_url ||
            `${assetPrefix}/static/images/empty-store-bot.webp`;

      return {
        showRating: !!rating,
        rawRating: rating,
        showSold,
        soldDisplay,
        imageSrc,
      };
    }, [item]);

  const handleOrderNow = useCallback(
    (e: React.MouseEvent) => {
      onAddToCartClick?.();
      e.stopPropagation();
      if (isClosed) return;
      if (!isAuthenticated) return setShowLoginDialog(true);

      // Convert SoloMealItem sang MenuItem
      const menuItem = soloMealItemToMenuItem(item);

      // Extract metadata để tracking
      const metadata = extractSoloMealMetadata(item);

      // Mở sheet với tất cả thông tin cần thiết
      openCartSheet(menuItem, {
        storeId: item.store_id.toString(),
        pageId: item.store_id.toString(),
        slug: item.store_slug,
        branchId: '',
        tag: 'solomeal',
        metadata: {
          ...metadata,
          item_index: index, // Thêm index vào metadata
        },
      });

      // Navigate to store detail
      if (!isSoloMeal) {
        router.push(routes.storeDetail(item.store_slug));
      }
    },
    [
      onAddToCartClick,
      isClosed,
      isAuthenticated,
      setShowLoginDialog,
      item,
      openCartSheet,
      index,
      isSoloMeal,
      router,
      routes,
    ]
  );

  const handleCardClick = useCallback(() => {
    if (!isAuthenticated) return setShowLoginDialog(true);
    onClick?.();
    if (!isClosed) router.push(routes.storeDetail(item.store_slug));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isAuthenticated,
    isClosed,
    router,
    routes,
    setShowLoginDialog,
    item.store_slug,
  ]);

  return (
    <Card
      ref={impressionRef}
      onClick={handleCardClick}
      className="size-full overflow-hidden relative bg-white flex flex-col p-0 border-none"
    >
      {item.is_ai_image && (
        <Badge className="z-50 size-7 absolute top-2 right-2 bg-white/50 hover:bg-white/90 text-white border-none shadow-sm p-1 text-[10px] font-bold">
          <Image
            fill
            className="p-1"
            src={`${assetPrefix}/static/icons/white-ai.svg`}
            alt={''}
          />
        </Badge>
      )}
      {/* Image/Video */}
      <VideoPreviewCard
        videoSrc={item.solo_meal_video_url}
        imageSrc={imageSrc}
        mode={videoMode}
        alt={item.name}
        is_ai_image={item.is_ai_image}
      >
        {/* Children vẽ sau video → khung nằm trên cả ảnh lẫn video, dưới ETA. */}
        <ItemFrame src={item.frame_image_url} />
        {isClosed && <InactiveBadge />}
        {/* ETA */}
        <div className="absolute bottom-0 left-0 flex items-end text-neutral-70">
          <Badge
            variant="secondary"
            className="flex items-center bg-white rounded-none rounded-tr-2xl h-5"
          >
            <img
              src={`${assetPrefix}/static/icons/clock.svg`}
              alt="ETA"
              className="mr-1 size-3"
            />
            <span className="text-[10px] font-semibold pr-1">
              Từ {calculateETA(cityId, item.distance) ?? 20} phút
            </span>
          </Badge>
          <img
            src={`${assetPrefix}/static/icons/badge-tail.svg`}
            alt="tail"
            className="size-4 object-contain -mb-0.5 -ml-0.5"
          />
        </div>
      </VideoPreviewCard>

      {/* Info */}
      <div className="py-2 flex flex-col gap-1 flex-1">
        <h3 className="font-medium text-sm text-neutral-90 line-clamp-1">
          {item.trend_item ? (
            <TrendingIcon className="inline-block mr-1 animate-fire-pulse" />
          ) : null}{' '}
          {item.name}
        </h3>

        {/* Price & Button */}
        <div className="flex justify-between items-center border-b pb-2 mt-auto">
          <div>
            {item.discounted_price && item.discounted_price < item.price && (
              <span className="text-xs text-neutral-40 line-through">
                {formatCurrency(item.price)}
              </span>
            )}
            <span className="block font-bold text-sm bg-[#FEBC2F] px-1 shadow-4">
              {formatCurrency(item.discounted_price || item.price)}
            </span>
          </div>
          <Button
            onClick={handleOrderNow}
            variant="outline"
            disabled={isClosed}
            className="text-primary shadow-4 rounded-xl font-bold py-1 h-8"
          >
            Thêm
          </Button>
        </div>

        {/* Store info */}
        <div className="text-xs text-neutral-60 mb-1 flex justify-between">
          <span className="truncate">{item.store_name}</span>
          <span>{formatDistanceMeterKilometer(item.distance)}</span>
        </div>

        {/* Rating & Sold */}
        {(showRating || showSold) && (
          <Badge
            variant="secondary"
            className="flex items-center text-xs text-neutral-60 bg-gradient-to-r from-secondary-20/80 to-white border-none shadow-none px-1"
          >
            {showRating && (
              <span className="flex items-center gap-1">
                {item.rating?.total !== 0 ? (
                  <Star className="size-3 fill-[#FEBC2F] text-[#FEBC2F]" />
                ) : (
                  <img
                    src={`${assetPrefix}/static/images/gg-rating.webp`}
                    alt="Store"
                    className="inline-block mr-1 h-auto w-4 mb-0.5"
                  />
                )}
                <span className="font-bold">{rawRating!.toFixed(1)}</span>
              </span>
            )}
            {showRating && showSold && <span className="mx-1">|</span>}
            {showSold && (
              <span className="flex items-center">
                <img
                  src={`${assetPrefix}/static/icons/bag.svg`}
                  alt="sold"
                  className="size-4 mr-1"
                />
                {soldDisplay} lượt bán
              </span>
            )}
          </Badge>
        )}
      </div>
    </Card>
  );
}
